import { useContext } from 'react';
import { get, isEmpty, uniq } from 'lodash';
import { useSearchParams } from 'react-router-dom';
import { DashContext } from 'src/pagesDashboard/Dash/context/DashProvider';
import useMyProfileInitialState from 'src/pagesDashboard/Dash/context/state';
import { useReviewerAndRevieweeHistory } from 'src/pagesDashboard/UserProfile/queries';
import { useAccountScore } from 'src/queries/account';
import { useCompany } from 'src/queries/company';
import { useFeedback } from 'src/queries/feedback';
import { useReportScore } from 'src/queries/reports';
import { appUtils } from 'src/components/index';
import commonQuestions from 'common/commonQuestions';

const getFilterByRoles = (reviewedRoles, company, rolesFilter) => {
  if (
    !company
    || !company.questions
    || !reviewedRoles
    || !reviewedRoles.length
  ) {
    return [];
  }
  return (
    reviewedRoles
    && reviewedRoles
      .filter((roleId) => commonQuestions.getRoleById(roleId, company.questions))
      .map((roleId) => {
        const roleObj = commonQuestions.getRoleById(roleId, company.questions);
        return {
          id: roleId,
          label: roleObj.label,
          name: roleObj.label,
          checked: rolesFilter && Boolean(rolesFilter.includes(roleId))
        };
      })
      .sort((a, b) => (a.label < b.label ? -1 : 1))
  );
};

export const useMyProfile = () => {
  const contextObject = useContext(DashContext);
  const defaultRange = appUtils.getDefaultRange();
  const newMyProfileInitialState = {
    ...useMyProfileInitialState,
    range: defaultRange
  };
  const context = get(contextObject, 'context', newMyProfileInitialState);
  const [matches] = useSearchParams();
  const reportId = matches.get('reportId');

  const {
    data: company,
    isFetching: isFetchingCompany,
    isError: isErrorCompany,
    isFetched: isFetchedCompany
  } = useCompany();
  const companyQuestions = get(company, 'questions', {});

  const userId = appUtils.getLoggedUserId();
  const range = context.range.value;
  const { start } = context.range;
  const { end } = context.range;

  const {
    data: { feedbacks: feedbackData } = {
      feedbacks: [],
      notes: []
    },
    isFetching: isFetchingFeedback,
    isError: isErrorFeedback,
    isFetched: isFetchedFeedback
  } = useFeedback({
    ids: [userId],
    range,
    start,
    end
  });

  const {
    data: score,
    isFetching: isFetchingScore,
    isError: isErrorScore,
    isFetched: isFetchedScore
  } = useAccountScore(
    {
      id: userId,
      ...(start && {
        start,
        end
      })
    },
    {
      bundleCategories: true
    }
  );

  const {
    data: reportScore,
    isFetching: isFetchingReportScore,
    isError: isErrorReportScore,
    isFetched: isFetchedReportScore
  } = useReportScore(reportId);

  const scoreToGetCategoriesFrom = reportId ? reportScore : score;
  const bundledCategories = get(
    scoreToGetCategoriesFrom,
    'bundledCategories',
    []
  ).map((bundledCategory) => {
    const categoryData = commonQuestions.getCategory(
      bundledCategory.id,
      companyQuestions.CATEGORIES
    );

    if (!isEmpty(categoryData)) {
      return {
        ...bundledCategory,
        label: categoryData.label
      };
    }

    return {
      ...bundledCategory,
      label: 'Unknown'
    };
  });

  let feedbackReceived = '-';
  if (feedbackData) {
    feedbackReceived = feedbackData;
  }

  const rolesFilter = null;
  const reviewedRoleIds = uniq(
    bundledCategories.map((cat) => cat.roleIds).flat()
  );

  const filterByRoles = getFilterByRoles(reviewedRoleIds, company, rolesFilter);

  const {
    reviewers,
    reviewees,
    revieweeRoles,
    revieweeCategories: allRevieweeCategories,
    isFetching: isFetchingReviewerAndRevieweeHistory,
    isError: isErrorReviewerAndRevieweeHistory,
    isFetched: isFetchedReviewerAndRevieweeHistory
  } = useReviewerAndRevieweeHistory(userId, {
    start,
    end
  });

  const { roles: filteredRoles } = context.filters;
  const revieweeRolesOptions = revieweeRoles.map((role) => ({
    id: role.id,
    label: role.label,
    checked: filteredRoles?.includes(role.id)
  }));
  let revieweeCategories = [];
  if (filteredRoles?.length) {
    const validCategoryIds = revieweeRoles
      .filter((role) => filteredRoles.includes(role.id))
      .map((role) => role.categories)
      .flat();
    revieweeCategories = allRevieweeCategories.filter((cat) => validCategoryIds.includes(cat.id));
  } else revieweeCategories = allRevieweeCategories;

  return {
    isFetchingScore,
    feedbackReceived,
    filterByRoles,
    revieweeRolesOptions,
    revieweeCategories,
    isFetching:
      isFetchingCompany
      || isFetchingFeedback
      || isFetchingScore
      || isFetchingReviewerAndRevieweeHistory
      || isFetchingReportScore,
    isError:
      isErrorCompany
      || isErrorFeedback
      || isErrorReviewerAndRevieweeHistory
      || isErrorScore
      || isErrorReportScore,
    isFetched:
      isFetchedCompany
      || isFetchedFeedback
      || isFetchedScore
      || isFetchedReviewerAndRevieweeHistory
      || isFetchedReportScore,
    reviewees,
    reviewers,
    score,
    bundledCategories
  };
};

export default useMyProfile;
